/**
 * Product Quantity Limits (ES Module)
 *
 * Caps how many units of a given product a customer may have in a single
 * order. Configured in Theme settings → Cart → Purchase limits, and handed
 * to this script by `snippets/product-quantity-limits.liquid`.
 *
 * Currently used for the Dragons Den Bundle (SKU SKDD15, product ID
 * 9674703569127), which is limited to 1 per order.
 *
 * Strategy — two layers:
 *
 *   1. Pre-emptive. `snippets/quantity-selector.liquid` renders max="1" on the
 *      quantity input for a limited product, on the product page and on every
 *      cart line, so the stepper cannot go past the limit. This module also
 *      monkey-patches `CartItemsComponent.prototype.updateQuantity` so a typed
 *      value is clamped before the /cart/change request is sent.
 *
 *   2. Reconciliation. On every `cart:update` the cart is re-read and any line
 *      over the limit is reset via /cart/update, whatever put it there —
 *      product form, quick add, a second tab, or a /cart/add permalink. This is
 *      the layer that actually enforces the rule; layer 1 is UX polish.
 *
 * A polite notice is then rendered at the top of the cart drawer and the cart
 * page explaining what happened.
 *
 * Note: this is client-side only, so it cannot stop a hand-crafted POST to
 * /cart/add. Blocking that requires a Shopify Function (Cart and Checkout
 * Validation) on top of this.
 */

import { CartUpdateEvent } from '@theme/events';

const SOURCE = 'product-quantity-limits';
const PATCHED = Symbol.for('product-quantity-limits.patched');
const STEPPER_PATCHED = Symbol.for('product-quantity-limits.stepper-patched');
const MAX = 1;
const DEFAULT_MESSAGE =
  "[product] is limited to 1 per customer";

/** How long the notice stays on screen before it hides itself. */
const NOTICE_DURATION = 5000;

/**
 * The notice currently on show, as text plus the moment it should disappear.
 * A cart request morphs the cart sections, which throws away anything injected
 * into them, so the notice is re-asserted from this until its time is up.
 * @type {{ text: string, expiresAt: number } | null}
 */
let activeNotice = null;

/** @type {number | undefined} */
let noticeWatcher;

const config = window.ProductQuantityLimits ?? {};
const limitedProductIds = new Set((config.onePerOrderProductIds ?? []).map(String));
const messageTemplate = config.message || DEFAULT_MESSAGE;

/**
 * Line number (1-based) → product title, for the lines currently under a limit.
 * Seeded from Liquid so the clamp below works from first paint, then refreshed
 * from /cart.js on every reconcile.
 */
const limitedLines = new Map(
  (config.cartLines ?? []).map(({ line, title }) => [line, title])
);

/** Product titles whose limit was hit, waiting to be shown once the cart has settled. */
const pendingNotices = new Set();

let reconciling = false;
/** @type {number | undefined} */
let reconcileTimer;

// ── Cart reads and writes ───────────────────────────────────────────

/**
 * Reads the current cart.
 * @returns {Promise<Object | null>}
 */
async function getCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
  });

  return response.ok ? response.json() : null;
}

/**
 * Collects the section IDs of every cart rendered on the page, so the sections
 * come back rendered with the corrected quantities.
 * @returns {string[]}
 */
function cartSectionIds() {
  const ids = Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => /** @type {HTMLElement} */ (element).dataset.sectionId)
    .filter(Boolean);

  return Array.from(new Set(/** @type {string[]} */ (ids)));
}

/**
 * Works out which lines are over the limit.
 *
 * The cap is per product across the whole order, so if a limited product
 * somehow ends up on more than one line, the first line keeps the allowance
 * and the rest are zeroed.
 *
 * @param {Object} cart
 * @returns {{ updates: Record<string, number>, titles: Set<string> }}
 */
function planUpdates(cart) {
  /** @type {Record<string, number>} */
  const updates = {};
  const titles = new Set();
  const allowance = new Map();

  for (const item of cart.items) {
    const productId = String(item.product_id);
    if (!limitedProductIds.has(productId)) continue;

    const remaining = allowance.get(productId) ?? MAX;
    const allowed = Math.max(0, Math.min(item.quantity, remaining));

    allowance.set(productId, remaining - allowed);

    if (allowed !== item.quantity) {
      updates[item.key] = allowed;
      titles.add(item.product_title || item.title);
    }
  }

  return { updates, titles };
}

/**
 * Records which cart lines are limited, so `updateQuantity` can clamp by line
 * number without re-reading the cart.
 * @param {Object} cart
 */
function trackLimitedLines(cart) {
  limitedLines.clear();

  cart.items.forEach((item, index) => {
    if (limitedProductIds.has(String(item.product_id))) {
      limitedLines.set(index + 1, item.product_title || item.title);
    }
  });
}

/**
 * Applies the corrected quantities.
 * @param {Record<string, number>} updates
 * @returns {Promise<Object | null>}
 */
async function applyUpdates(updates) {
  const response = await fetch(Theme.routes.cart_update_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      updates,
      sections: cartSectionIds().join(','),
      sections_url: window.location.pathname,
    }),
  });

  return response.ok ? response.json() : null;
}

// ── Reconciliation ──────────────────────────────────────────────────

function scheduleReconcile() {
  if (reconcileTimer) clearTimeout(reconcileTimer);
  reconcileTimer = setTimeout(reconcile, 120);
}

async function reconcile() {
  // A cart change landed mid-flight — come back to it rather than dropping it.
  if (reconciling) {
    scheduleReconcile();
    return;
  }

  reconciling = true;

  try {
    const cart = await getCart();
    if (!cart) return;

    trackLimitedLines(cart);

    const { updates, titles } = planUpdates(cart);
    for (const title of titles) pendingNotices.add(title);

    if (Object.keys(updates).length > 0) {
      const corrected = await applyUpdates(updates);

      if (corrected) {
        trackLimitedLines(corrected);
        announceCartUpdate(corrected);

        await nextFrame();
      }
    }

    flushNotices();
  } catch (error) {
    console.error('[product-quantity-limits] Could not enforce the cart limit.', error);
  } finally {
    reconciling = false;
  }
}

/**
 * Lets the theme's own components morph themselves and refresh the cart bubble.
 *
 * `cart-drawer-component` treats every `cart:update` as a reason to auto-open,
 * so the attribute comes off for the duration of the dispatch: a correction the
 * customer did not ask for should not pop the cart open at them. If the drawer
 * is already open it stays open — morph preserves `<dialog>` open state.
 *
 * Tagged with our own source so the `cart:update` listener below ignores it.
 *
 * @param {Object} cart
 */
function announceCartUpdate(cart) {
  const autoOpenDrawers = document.querySelectorAll('cart-drawer-component[auto-open]');
  autoOpenDrawers.forEach((drawer) => drawer.removeAttribute('auto-open'));

  document.dispatchEvent(
    new CartUpdateEvent({}, SOURCE, {
      source: SOURCE,
      itemCount: cart.item_count,
      sections: cart.sections ?? {},
    })
  );

  // Listeners are synchronous, so the attribute is safe to put back now.
  autoOpenDrawers.forEach((drawer) => drawer.setAttribute('auto-open', ''));
}

/** Waits for the DOM to settle after a morph, without stalling a hidden tab. */
function nextFrame() {
  return new Promise((resolve) => {
    const done = () => resolve(undefined);

    requestAnimationFrame(() => requestAnimationFrame(done));
    setTimeout(done, 300);
  });
}

// ── Notice ──────────────────────────────────────────────────────────

function flushNotices() {
  if (pendingNotices.size === 0) return;

  const titles = Array.from(pendingNotices);
  pendingNotices.clear();

  showNotice(messageTemplate.replace('[product]', () => formatList(titles)));
}

/**
 * @param {string[]} items
 * @returns {string}
 */
function formatList(items) {
  if (items.length <= 1) return items[0] ?? '';

  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/**
 * Renders the notice at the top of every cart on the page — the drawer, the
 * cart page, or both.
 * @param {string} text
 */
function showNotice(text) {
  activeNotice = { text, expiresAt: Date.now() + NOTICE_DURATION };
  renderNotice(true);

  clearInterval(noticeWatcher);
  noticeWatcher = setInterval(watchNotice, 250);
}

/** Backstop for any re-render the `cart:update` listener does not cover. */
function watchNotice() {
  if (activeNotice && Date.now() < activeNotice.expiresAt) {
    renderNotice();
    return;
  }

  dismissNotices();
}

/**
 * Renders the active notice into any cart that is not already showing it, so a
 * cart that morphed mid-countdown gets it back without restarting the fade-in.
 */
function renderNotice(animate = false) {
  if (!activeNotice) return;

  ensureStyles();

  for (const component of document.querySelectorAll('cart-items-component')) {
    const host = component.querySelector('.cart-items__wrapper') ?? component;
    if (host.querySelector('.pql-notice')) continue;

    host.prepend(buildNotice(activeNotice.text, animate));
  }
}

/** Stops the countdown and fades out every notice on the page. */
function dismissNotices() {
  activeNotice = null;
  clearInterval(noticeWatcher);
  noticeWatcher = undefined;

  document.querySelectorAll('.pql-notice').forEach((notice) => {
    hideNotice(/** @type {HTMLElement} */ (notice));
  });
}

/**
 * @param {string} text
 * @param {boolean} [animate] - Fade in. Off when replacing one a morph removed,
 *   otherwise the restore reads as a flicker.
 * @returns {HTMLElement}
 */
function buildNotice(text, animate = false) {
  const notice = document.createElement('div');
  notice.className = animate ? 'pql-notice' : 'pql-notice pql-notice--restored';
  notice.setAttribute('role', 'status');

  const icon = document.createElement('span');
  icon.className = 'pql-notice__icon';
  icon.setAttribute('aria-hidden', 'true');
  icon.innerHTML =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="11" x2="12" y2="16"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>';

  const message = document.createElement('p');
  message.className = 'pql-notice__text';
  message.textContent = text;

  const dismiss = document.createElement('button');
  dismiss.type = 'button';
  dismiss.className = 'pql-notice__dismiss';
  dismiss.setAttribute('aria-label', 'Dismiss message');
  dismiss.innerHTML =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg>';
  dismiss.addEventListener('click', () => dismissNotices());

  notice.append(icon, message, dismiss);

  return notice;
}

/**
 * Fades the notice out and removes it.
 * @param {HTMLElement} notice
 */
function hideNotice(notice) {
  if (notice.classList.contains('pql-notice--leaving')) return;

  notice.classList.add('pql-notice--leaving');
  notice.addEventListener('animationend', () => notice.remove(), { once: true });

  // Reduced motion runs no animation, so animationend never fires.
  setTimeout(() => notice.remove(), 400);
}

function ensureStyles() {
  if (document.getElementById('pql-notice-styles')) return;

  const style = document.createElement('style');
  style.id = 'pql-notice-styles';
  style.textContent = [
    '.pql-notice {',
    '  display: flex; align-items: flex-start; gap: 10px;',
    '  background: #f5f2e2; color: #113e30;',
    '  border: 1px solid rgb(17 62 48 / 0.2); border-radius: 8px;',
    '  padding: 12px 14px; margin: 0 0 16px;',
    '  font-family: var(--cart-primary-font-family, inherit);',
    '  font-size: 0.8125rem; line-height: 1.45;',
    '  animation: pqlNoticeIn 200ms ease-out;',
    '}',
    '.pql-notice__icon { display: flex; flex-shrink: 0; margin-top: 1px; }',
    '.pql-notice__icon svg { width: 16px; height: 16px; }',
    '.pql-notice__text { margin: 0; flex: 1 1 auto; }',
    '.pql-notice__dismiss {',
    '  flex-shrink: 0; padding: 0; border: 0; background: none;',
    '  color: inherit; cursor: pointer; opacity: 0.6; line-height: 0;',
    '}',
    '.pql-notice__dismiss:hover { opacity: 1; }',
    '.pql-notice__dismiss svg { width: 14px; height: 14px; }',
    '@keyframes pqlNoticeIn {',
    '  from { opacity: 0; transform: translateY(-6px); }',
    '  to { opacity: 1; transform: translateY(0); }',
    '}',
    '.pql-notice--restored { animation: none; }',
    '.pql-notice--leaving { animation: pqlNoticeOut 200ms ease-in forwards; }',
    '@keyframes pqlNoticeOut {',
    '  from { opacity: 1; transform: translateY(0); }',
    '  to { opacity: 0; transform: translateY(-6px); }',
    '}',
    '@media (prefers-reduced-motion: reduce) {',
    '  .pql-notice, .pql-notice--leaving { animation: none; }',
    '}',
  ].join('\n');

  document.head.appendChild(style);
}

// ── Clamp before the cart request goes out ──────────────────────────

customElements.whenDefined('cart-items-component').then(() => {
  const CartItems = customElements.get('cart-items-component');
  if (!CartItems || CartItems.prototype[PATCHED]) return;

  const originalUpdateQuantity = CartItems.prototype.updateQuantity;

  /**
   * @param {{ line: number, quantity: number, action: string }} updateConfig
   */
  CartItems.prototype.updateQuantity = function (updateConfig) {
    const title = updateConfig ? limitedLines.get(updateConfig.line) : undefined;

    if (title && updateConfig.quantity > MAX) {
      pendingNotices.add(title);
      resetLineInput(this, updateConfig.line);

      return originalUpdateQuantity.call(this, { ...updateConfig, quantity: MAX });
    }

    return originalUpdateQuantity.call(this, updateConfig);
  };

  CartItems.prototype[PATCHED] = true;
});

// ── Explain the capped stepper ──────────────────────────────────────

/**
 * The quantity input carries max="1" for a limited product, so `stepUp()` is a
 * no-op and a typed value is clamped by the component itself. Either way no
 * cart request is made, so nothing above would ever tell the customer why the
 * number will not move. These patches show the notice on that blocked action.
 */
customElements.whenDefined('quantity-selector-component').then(() => {
  const QuantitySelector = customElements.get('quantity-selector-component');
  if (!QuantitySelector || QuantitySelector.prototype[STEPPER_PATCHED]) return;

  const originalIncrease = QuantitySelector.prototype.increaseQuantity;
  const originalSet = QuantitySelector.prototype.setQuantity;

  QuantitySelector.prototype.increaseQuantity = function (event) {
    noticeIfCapped(this, Number(this.refs?.quantityInput?.value) + 1);

    return originalIncrease.call(this, event);
  };

  QuantitySelector.prototype.setQuantity = function (event) {
    const typed = event?.target instanceof HTMLInputElement ? Number(event.target.value) : NaN;
    noticeIfCapped(this, typed);

    return originalSet.call(this, event);
  };

  QuantitySelector.prototype[STEPPER_PATCHED] = true;
});

/**
 * Shows the notice when a wanted quantity is above the cap on a limited line.
 * @param {{ refs?: { quantityInput?: HTMLInputElement } }} component
 * @param {number} wanted
 */
function noticeIfCapped(component, wanted) {
  const input = component.refs?.quantityInput;
  if (!input || !Number.isFinite(wanted)) return;

  const max = Number(input.max);
  if (!max || wanted <= max) return;

  // Only lines holding a limited product get a notice — a product with its own
  // quantity rule is not our business.
  const title = limitedLines.get(Number(input.dataset.cartLine));
  if (!title) return;

  pendingNotices.add(title);
  flushNotices();
}

/**
 * Snaps the visible input back to the limit while the request is in flight.
 * @param {{ refs?: { quantitySelectors?: Element[] } }} component
 * @param {number} line
 */
function resetLineInput(component, line) {
  const input = component.refs?.quantitySelectors?.[line - 1]?.querySelector('input');

  if (input instanceof HTMLInputElement) input.value = String(MAX);
}

// ── Init ────────────────────────────────────────────────────────────

// Nothing configured — stay out of the way entirely.
if (limitedProductIds.size > 0) {
  document.addEventListener('cart:update', (event) => {
    // Restoring the notice takes three passes because the cart re-renders at
    // three different moments:
    //
    //   1. now — other cart components morph inside their own `cart:update`
    //      listeners, which ran just before this one.
    //   2. microtask — `updateQuantity` dispatches this event and only *then*
    //      calls morphSection(), so the wipe is still ahead of us. A microtask
    //      lands after that call but still before the browser paints.
    //   3. next frame — the slower path, where the section is re-fetched.
    //
    // None of these replay the fade-in, so the notice simply stays put.
    renderNotice();
    queueMicrotask(() => renderNotice());
    nextFrame().then(() => renderNotice());

    const detail = /** @type {CustomEvent} */ (event).detail;
    if (detail?.data?.source === SOURCE) return;

    scheduleReconcile();
  });

  // Liquid has already checked the cart for this page render, so we only reach
  // for the network when there is something to fix.
  if (config.cartOverLimit) scheduleReconcile();
}
